/* ==========================================================================
   Frénza Luxe — theme.js (vanilla, no dependencies)
   ========================================================================== */
(() => {
  'use strict';

  const F = window.Frenza || {};
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const overlay = $('[data-overlay]');

  /* ---------- Money ---------- */
  function formatMoney(cents, format = F.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = (n, d = 2, t = ',', dec = '.') => {
      n = (n / 100).toFixed(d);
      const [i, f] = n.split('.');
      return i.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${t}`) + (f ? dec + f : '');
    };
    const m = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!m) return cents;
    const map = {
      amount: fmt(cents, 2),
      amount_no_decimals: fmt(cents, 0),
      amount_with_comma_separator: fmt(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: fmt(cents, 0, ' ')
    };
    return format.replace(m[0], map[m[1]] ?? fmt(cents, 2));
  }

  /* ---------- Drawers ---------- */
  let activeDrawer = null;
  let lastFocus = null;
  function openDrawer(el) {
    if (!el) return;
    if (activeDrawer && activeDrawer !== el) closeDrawer(false);
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    overlay && overlay.classList.add('is-active');
    document.body.classList.add('is-locked');
    document.body.classList.toggle('header-drawer-open', !!el.closest('.section-header'));
    activeDrawer = el;
    setTimeout(() => el.focus(), 50);
  }
  function closeDrawer(restore = true) {
    if (!activeDrawer) return;
    activeDrawer.classList.remove('is-open');
    overlay && overlay.classList.remove('is-active');
    document.body.classList.remove('is-locked', 'header-drawer-open');
    activeDrawer = null;
    if (restore && lastFocus) lastFocus.focus();
  }
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(document.getElementById(opener.dataset.drawerOpen)); return; }
    if (e.target.closest('[data-drawer-close]') || e.target === overlay) closeDrawer();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closeDrawer(); closeSearch(); }
  });

  /* ---------- Cart ---------- */
  const cartDrawer = () => $('[data-cart-drawer]');
  const SECTION_ID = 'cart-drawer';

  function updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', Number(count) === 0);
      el.classList.add('is-bump');
      setTimeout(() => el.classList.remove('is-bump'), 300);
    });
  }

  function renderDrawer(sections) {
    const html = sections && sections[SECTION_ID];
    const drawer = cartDrawer();
    if (!html || !drawer) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-drawer-inner]');
    const current = drawer.querySelector('[data-cart-drawer-inner]');
    if (fresh && current) {
      current.replaceWith(fresh);
      updateCount(fresh.dataset.itemCount || 0);
    }
  }

  async function refreshDrawer() {
    const res = await fetch(`${F.routes.root}?sections=${SECTION_ID}`);
    renderDrawer(await res.json());
  }

  async function addToCart(form, button) {
    const fd = new FormData(form);
    fd.append('sections', SECTION_ID);
    fd.append('sections_url', window.location.pathname);
    const label = button && button.querySelector('span');
    const original = label ? label.textContent : '';
    if (button) { button.classList.add('is-loading'); if (label) label.textContent = F.strings.adding; }
    try {
      const res = await fetch(`${F.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Error');
      if (F.cartType === 'drawer' && cartDrawer()) {
        renderDrawer(data.sections);
        openDrawer(cartDrawer());
      } else {
        window.location.href = F.routes.cart;
      }
    } catch (err) {
      showFormError(form, err.message);
    } finally {
      if (button) { button.classList.remove('is-loading'); if (label) label.textContent = original; }
    }
  }

  function showFormError(form, msg) {
    let el = form.querySelector('[data-form-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'form-error';
      el.setAttribute('data-form-error', '');
      form.appendChild(el);
    }
    el.textContent = msg;
    setTimeout(() => el.remove(), 5000);
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-product-form], form[action*="/cart/add"]');
    if (!form || form.hasAttribute('data-no-ajax')) return;
    e.preventDefault();
    addToCart(form, e.submitter || form.querySelector('[type="submit"]'));
  });

  let changeQueue = Promise.resolve();
  function changeLine(line, quantity) {
    const drawer = cartDrawer();
    changeQueue = changeQueue.then(async () => {
      drawer && drawer.classList.add('is-loading');
      try {
        const res = await fetch(`${F.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: [SECTION_ID], sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description);
        renderDrawer(data.sections);
      } catch (err) {
        await refreshDrawer();
      } finally {
        drawer && drawer.classList.remove('is-loading');
      }
    });
  }

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-cart-toggle]');
    if (toggle && F.cartType === 'drawer' && cartDrawer() && !document.body.classList.contains('template-cart')) {
      e.preventDefault();
      openDrawer(cartDrawer());
      return;
    }
    const qtyBtn = e.target.closest('[data-cart-drawer] [data-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      changeLine(Number(qtyBtn.dataset.line), Math.max(0, Number(qtyBtn.dataset.qtyChange)));
    }
  });

  let noteTimer;
  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      fetch(`${F.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: e.target.value }) });
    }, 500);
  });

  /* ---------- Generic quantity inputs (product + cart page) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const input = btn.closest('[data-qty]').querySelector('input');
    const step = Number(btn.dataset.qtyStep);
    const min = Number(input.min || 0);
    input.value = Math.max(min, Number(input.value || 0) + step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Footer accordions collapsed on mobile ---------- */
  if (window.matchMedia('(max-width: 989px)').matches) {
    $$('.footer details.footer__col').forEach((d) => d.removeAttribute('open'));
  }

  /* ---------- Header ---------- */
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Search + predictive search ---------- */
  const searchPanel = $('[data-search-panel]');
  function openSearch() {
    if (!searchPanel) return;
    searchPanel.hidden = false;
    const input = $('[data-predictive-input]', searchPanel);
    setTimeout(() => input && input.focus(), 30);
  }
  function closeSearch() { if (searchPanel) searchPanel.hidden = true; }
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-open]')) { e.preventDefault(); searchPanel && searchPanel.hidden ? openSearch() : closeSearch(); }
    if (e.target.closest('[data-search-close]')) closeSearch();
  });
  const pInput = $('[data-predictive-input]');
  const pResults = $('[data-predictive-results]');
  if (pInput && pResults) {
    let t;
    pInput.addEventListener('input', () => {
      clearTimeout(t);
      const q = pInput.value.trim();
      if (q.length < 2) { pResults.innerHTML = ''; return; }
      t = setTimeout(async () => {
        try {
          const url = `${F.routes.predictiveSearch}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
          const res = await fetch(url);
          const data = await res.json();
          const products = (data.resources && data.resources.results.products) || [];
          if (!products.length) { pResults.innerHTML = `<p class="empty-state" style="padding:16px 0">No results for “${q.replace(/</g, '')}”</p>`; return; }
          pResults.innerHTML = `<div class="predictive">${products.map((p) => `
            <a class="predictive__item" href="${p.url}">
              ${p.image ? `<img src="${p.image}&width=160" alt="" loading="lazy">` : ''}
              <span><span class="predictive__title">${p.title}</span><br><span class="predictive__price">${formatMoney(Math.round(parseFloat(p.price) * 100))}</span></span>
            </a>`).join('')}
            <a class="link-arrow predictive__all" href="${F.routes.search}?q=${encodeURIComponent(q)}">View all results</a></div>`;
        } catch (err) { pResults.innerHTML = ''; }
      }, 250);
    });
  }

  /* ---------- Section initialisers (re-run in theme editor) ---------- */
  function initSections(scope = document) {
    /* ---------- Announcement ---------- */
    $$('[data-announcement]', scope).forEach((root) => {
      const msgs = $$('.announcement__msg', root);
      if (msgs.length < 2) return;
      let i = 0;
      const go = (n) => {
        msgs[i].classList.remove('is-active');
        msgs[i].classList.add('is-leaving');
        const prev = msgs[i];
        setTimeout(() => prev.classList.remove('is-leaving'), 500);
        i = (n + msgs.length) % msgs.length;
        msgs[i].classList.add('is-active');
      };
      let timer = setInterval(() => go(i + 1), Number(root.dataset.speed) || 5000);
      const reset = () => { clearInterval(timer); timer = setInterval(() => go(i + 1), Number(root.dataset.speed) || 5000); };
      $('[data-ann-next]', root)?.addEventListener('click', () => { go(i + 1); reset(); });
      $('[data-ann-prev]', root)?.addEventListener('click', () => { go(i - 1); reset(); });
    });

    /* ---------- Hero slideshow ---------- */
    $$('[data-slideshow]', scope).forEach((root) => {
      const slides = $$('[data-slide]', root);
      const dots = $$('[data-dot]', root);
      if (slides.length < 2) return;
      const speed = Number(root.dataset.speed) || 6000;
      root.style.setProperty('--hero-speed', `${speed}ms`);
      let i = 0, timer;
      const go = (n) => {
        slides[i].classList.remove('is-active');
        dots[i] && dots[i].classList.remove('is-active');
        i = (n + slides.length) % slides.length;
        slides[i].classList.add('is-active');
        if (dots[i]) { dots[i].classList.remove('is-active'); void dots[i].offsetWidth; dots[i].classList.add('is-active'); }
      };
      const play = () => { if (root.dataset.autoplay === 'true') { clearInterval(timer); timer = setInterval(() => go(i + 1), speed); } };
      dots.forEach((d) => d.addEventListener('click', () => { go(Number(d.dataset.dot)); play(); }));
      $('[data-next]', root)?.addEventListener('click', () => { go(i + 1); play(); });
      $('[data-prev]', root)?.addEventListener('click', () => { go(i - 1); play(); });
      let x0 = null;
      root.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      root.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 45) { go(i + (dx < 0 ? 1 : -1)); play(); }
        x0 = null;
      });
      play();
      // Theme editor: jump to selected block
      document.addEventListener('shopify:block:select', (e) => {
        const idx = slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (idx > -1) { clearInterval(timer); go(idx); }
      });
    });

    /* ---------- Tabs ---------- */
    $$('[data-tabs]', scope).forEach((root) => {
      const tabs = $$('[data-tab]', root);
      const panels = $$('[data-tab-panel]', root);
      tabs.forEach((tab) => tab.addEventListener('click', () => {
        tabs.forEach((t) => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
        panels.forEach((p) => { p.classList.remove('is-active'); p.hidden = true; });
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');
        const panel = panels.find((p) => p.dataset.tabPanel === tab.dataset.tab);
        if (panel) { panel.hidden = false; panel.classList.add('is-active'); }
      }));
    });

    /* ---------- Horizontal scrollers ---------- */
    $$('[data-scroller]', scope).forEach((root) => {
      const track = $('[data-scroller-track]', root);
      const step = () => (track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width + 24 : 300);
      $('[data-scroller-next]', root)?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      $('[data-scroller-prev]', root)?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    });

    /* ---------- Countdown ---------- */
    $$('[data-countdown]', scope).forEach((el) => {
      const end = new Date(el.dataset.countdown.replace(' ', 'T')).getTime();
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const diff = end - Date.now();
        if (isNaN(end) || diff <= 0) { el.remove(); clearInterval(t); return; }
        $('[data-cd-days]', el).textContent = pad(Math.floor(diff / 864e5));
        $('[data-cd-hours]', el).textContent = pad(Math.floor((diff % 864e5) / 36e5));
        $('[data-cd-mins]', el).textContent = pad(Math.floor((diff % 36e5) / 6e4));
        $('[data-cd-secs]', el).textContent = pad(Math.floor((diff % 6e4) / 1e3));
      };
      const t = setInterval(tick, 1000);
      tick();
    });

    /* ---------- Reveal on scroll ---------- */
    if (document.body.classList.contains('has-animations') && 'IntersectionObserver' in window && !window.Shopify?.designMode) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      $$('[data-reveal]', scope).forEach((el) => io.observe(el));
    } else {
      $$('[data-reveal]', scope).forEach((el) => el.classList.add('is-visible'));
    }


  }
  initSections();

  /* ---------- Copy discount code ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    navigator.clipboard?.writeText(btn.dataset.copy).then(() => {
      const l = $('[data-copy-label]', btn);
      if (l) { const o = l.textContent; l.textContent = 'Copied!'; setTimeout(() => (l.textContent = o), 1800); }
    });
  });

  /* ---------- Product page ---------- */
  $$('[data-product]').forEach((root) => {
    const json = $('[data-product-json]', root);
    if (!json) return;
    const product = JSON.parse(json.textContent);
    const form = $('[data-product-form]', root);
    const idInput = form && $('input[name="id"]', form);
    const atc = form && $('[data-atc]', form);
    const atcLabel = atc && $('span', atc);
    const priceWrap = $('[data-price]', root);
    const stickyPrice = $('[data-sticky-price]');
    const stockNote = $('[data-stock-note]', root);
    const slides = $$('[data-gallery-slide]', root);
    const thumbs = $$('[data-gallery-thumb]', root);
    let current = 0;

    const showSlide = (idx) => {
      if (idx < 0 || idx >= slides.length) return;
      slides[current]?.classList.remove('is-active');
      thumbs[current]?.classList.remove('is-active');
      current = idx;
      slides[current].classList.add('is-active');
      thumbs[current]?.classList.add('is-active');
      thumbs[current]?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    };
    thumbs.forEach((t, idx) => t.addEventListener('click', () => showSlide(idx)));
    $('[data-gallery-prev]', root)?.addEventListener('click', () => showSlide((current - 1 + slides.length) % slides.length));
    $('[data-gallery-next]', root)?.addEventListener('click', () => showSlide((current + 1) % slides.length));
    const main = $('.gallery__main', root);
    if (main) {
      let x0 = null;
      main.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      main.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) showSlide((current + (dx < 0 ? 1 : -1) + slides.length) % slides.length);
        x0 = null;
      });
    }

    const selected = () => $$('[data-option-index]', root).map((fs) => {
      const c = $('input:checked', fs);
      return c ? c.value : null;
    });

    const update = () => {
      const opts = selected();
      const variant = product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      $$('[data-option-index]', root).forEach((fs) => {
        const c = $('input:checked', fs);
        const lbl = $('[data-selected-value]', fs);
        if (c && lbl) lbl.textContent = c.value;
      });
      // mark unavailable combinations
      $$('[data-option-index]', root).forEach((fs) => {
        const oi = Number(fs.dataset.optionIndex);
        $$('input', fs).forEach((inp) => {
          const test = opts.slice(); test[oi] = inp.value;
          const ok = product.variants.some((v) => v.available && v.options.every((o, i) => o === test[i]));
          inp.classList.toggle('is-unavailable', !ok);
        });
      });
      if (!variant) {
        if (atc) { atc.disabled = true; atcLabel.textContent = F.strings.unavailable; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (atc) { atc.disabled = !variant.available; atcLabel.textContent = variant.available ? F.strings.addToCart : F.strings.soldOut; }
      const priceHtml = `<span class="price__current">${formatMoney(variant.price)}</span>` +
        (variant.compare_at_price > variant.price ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s><span class="price__save">Save ${Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price)}%</span>` : '');
      if (priceWrap) { priceWrap.innerHTML = priceHtml; priceWrap.classList.toggle('price--sale', variant.compare_at_price > variant.price); }
      if (stickyPrice) stickyPrice.innerHTML = `<span class="price__current">${formatMoney(variant.price)}</span>`;
      if (stockNote) {
        const inv = Number((root.querySelector(`[data-inventory="${variant.id}"]`) || {}).value || 99);
        stockNote.hidden = !(variant.available && inv > 0 && inv <= 5);
        if (!stockNote.hidden) stockNote.textContent = `Only ${inv} left in stock`;
      }
      if (variant.featured_media) {
        const idx = slides.findIndex((s) => s.dataset.mediaId == variant.featured_media.id);
        if (idx > -1) showSlide(idx);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };
    root.addEventListener('change', (e) => { if (e.target.closest('[data-option-index]')) update(); });

    // Sticky add to cart (mobile)
    const sticky = $('[data-sticky-atc]');
    if (sticky && atc) {
      const io = new IntersectionObserver(([en]) => sticky.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0));
      io.observe(atc);
      $('[data-sticky-btn]', sticky)?.addEventListener('click', () => {
        if (form.requestSubmit) form.requestSubmit(atc); else atc.click();
      });
    }
  });

  /* ---------- Collection: sort + filters ---------- */
  $$('[data-sort]').forEach((sel) => sel.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }));
  $$('[data-filter-form]').forEach((form) => {
    form.addEventListener('change', (e) => {
      if (e.target.type === 'checkbox') form.requestSubmit ? form.requestSubmit() : form.submit();
    });
  });
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-filters-open]')) { $('[data-filters-drawer]')?.classList.add('is-open'); overlay?.classList.add('is-active'); }
    if (e.target.closest('[data-filters-close]') || e.target === overlay) { $('[data-filters-drawer]')?.classList.remove('is-open'); }
  });

  /* ---------- Cart page qty auto-submit ---------- */
  $$('[data-cart-form] input[name="updates[]"]').forEach((inp) => {
    let t;
    inp.addEventListener('change', () => { clearTimeout(t); t = setTimeout(() => inp.form.submit(), 400); });
  });

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    initSections(e.target);
    $$('[data-reveal]', e.target).forEach((el) => el.classList.add('is-visible'));
  });
})();
